import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { SendPhoneVerificationCodeRequest, SmsCodeSentEvent } from 'src/app/models/phoneVerification.model';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { UserService } from 'src/app/services/user.service';
import { SMS_CONSENT_VERSION, SMS_COUNTRY_OPTIONS } from 'src/app/shared/constants/smsOptIn.constants';
import { formatNanpLocalNumber, isValidNanpLocalNumber, toE164 } from 'src/app/shared/utils/phone-number.util';
import { trackById } from 'src/app/shared/utils/track-by.util';

@Component({
    standalone: false,
    selector: 'app-account-sms-opt-in',
    templateUrl: './account-sms-opt-in.component.html',
    styleUrls: ['./account-sms-opt-in.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class AccountSmsOptInComponent implements OnInit, OnDestroy {
    @Input() phoneNumber: string | null = null;
    @Output() cancelled = new EventEmitter<void>();
    @Output() codeSent = new EventEmitter<SmsCodeSentEvent>();

    readonly countryOptions = SMS_COUNTRY_OPTIONS;
    readonly trackById = trackById;

    country = SMS_COUNTRY_OPTIONS[0].id;
    localNumber = '';
    consentChecked = false;
    phoneTouched = false;
    sending = false;
    phoneInvalid = false;
    canSubmit = false;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private userService: UserService,
        private toastService: ToastService,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        this.localNumber = formatNanpLocalNumber(this.phoneNumber);
        this.refreshState();
    }

    onPhoneInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.localNumber = formatNanpLocalNumber(input.value);
        input.value = this.localNumber;
        this.refreshState();
    }

    onPhoneBlur(): void {
        this.phoneTouched = true;
        this.refreshState();
    }

    onConsentChange(event: Event): void {
        this.consentChecked = (event.target as HTMLInputElement).checked;
        this.refreshState();
    }

    submit(): void {
        this.phoneTouched = true;
        this.refreshState();
        if (!this.canSubmit) return;

        const e164Number = this.toE164Number();
        const consent: SendPhoneVerificationCodeRequest = {
            consentGivenAt: new Date().toISOString(),
            consentVersion: SMS_CONSENT_VERSION,
            consentLanguage: this.translateService.getCurrentLanguage(),
        };
        this.sending = true;
        this.refreshState();
        this.userService
            .patch({ phoneNumber: e164Number })
            .pipe(
                switchMap(() => this.userService.sendPhoneVerificationCode(consent)),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: () => {
                    this.sending = false;
                    this.codeSent.emit({ phoneNumber: e164Number, consent });
                },
                error: (err: HttpErrorResponse) => {
                    this.sending = false;
                    this.refreshState();
                    this.toastService.showError(
                        err,
                        this.translateService.getDynamicTranslation('Failed to send verification code.'),
                    );
                },
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private refreshState(): void {
        const valid = isValidNanpLocalNumber(this.localNumber);
        this.phoneInvalid = this.phoneTouched && this.localNumber.length > 0 && !valid;
        this.canSubmit = valid && this.consentChecked && !this.sending;
    }

    private toE164Number(): string {
        const dialCode = this.countryOptions.find((c) => c.id === this.country)?.dialCode;
        return toE164(this.localNumber, dialCode ?? SMS_COUNTRY_OPTIONS[0].dialCode);
    }
}
